/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CopyIcon from '@atlaskit/icon/core/copy';

import debugMessages from './messages';

interface CopyButtonProps {
	/** Text to copy to clipboard */
	text: string;
	/** Button label */
	label: string;
}

/**
 * A button that copies text to the clipboard and shows a brief confirmation.
 */
export const CopyButton = ({ text, label }: CopyButtonProps): JSX.Element => {
	const [copied, setCopied] = useState(false);
	const { formatMessage } = useIntl();

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// Fallback for environments where clipboard API is unavailable
		}
	}, [text]);

	return (
		<Button
			appearance="subtle"
			iconBefore={(iconProps) =>
				copied ? <CheckMarkIcon {...iconProps} /> : <CopyIcon {...iconProps} />
			}
			onClick={handleCopy}
			spacing="compact"
		>
			{copied ? formatMessage(debugMessages.copiedLabel) : label}
		</Button>
	);
};
